import { LightningBoltIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { ChatModel } from 'types';

import { chatModels } from '@/constants/chatModels';

export default function ModelMultiplierTable() {
  const { t } = useLocale('common');

  // Group models by multiplier
  const modelsByMultiplier = useMemo(() => {
    const groups: Record<string, { name: string; models: ChatModel[] }> = {
      '0': { name: 'Unlimited (0x)', models: [] },
      '0.25': { name: '0.25x', models: [] },
      '0.5': { name: '0.5x', models: [] },
      '0.75': { name: '0.75x', models: [] },
      '1': { name: 'Standard (1x)', models: [] },
      '3': { name: '3x', models: [] },
    };

    // Filter out hidden models and sort by title
    const visibleModels = [...chatModels]
      .filter((model) => !model.hidden)
      .sort((a, b) => a.title.localeCompare(b.title));

    // Group models by their multiplier
    visibleModels.forEach((model) => {
      const multiplier = (
        model.multiplier !== undefined ? model.multiplier : 1
      ).toString();
      if (groups[multiplier]) {
        groups[multiplier].models.push(model);
      } else {
        groups['1'].models.push(model); // Default to standard if not found
      }
    });

    // Convert to array and sort by multiplier (0 first, then ascending)
    return Object.entries(groups)
      .map(([key, value]) => ({
        multiplier: key,
        name: value.name,
        models: value.models,
      }))
      .sort((a, b) => {
        if (a.multiplier === '0') return -1;
        if (b.multiplier === '0') return 1;
        return parseFloat(a.multiplier) - parseFloat(b.multiplier);
      });
  }, []);

  return (
    <div className='overflow-hidden bg-white shadow dark:bg-slate-800 sm:rounded-lg'>
      <div className='flex items-center px-4 py-5 sm:px-6'>
        <LightningBoltIcon className='mr-2 h-6 w-6 text-primary-500' />
        <h3 className='text-lg font-medium leading-6 text-gray-900 dark:text-white'>
          {t('Model Word Consumption')}
        </h3>
      </div>
      <div className='border-t border-gray-200 dark:border-gray-700'>
        <dl>
          {modelsByMultiplier.map((group, groupIdx) => (
            <div
              key={group.multiplier}
              className={`px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 ${
                groupIdx % 2 === 0
                  ? 'bg-gray-50 dark:bg-slate-700'
                  : 'bg-white dark:bg-slate-800'
              }`}
            >
              <dt className='flex items-center text-sm font-medium text-gray-500 dark:text-gray-300'>
                {group.name}
                {group.multiplier === '0' && (
                  <span className='ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800'>
                    {t('Free')}
                  </span>
                )}
              </dt>
              <dd className='mt-1 text-sm text-gray-900 dark:text-gray-200 sm:col-span-2 sm:mt-0'>
                {group.models.length > 0 ? (
                  <ul className='grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3'>
                    {group.models.map((model) => (
                      <li key={model.slug} className='flex items-center'>
                        <model.icon className='mr-1 h-4 w-4' />
                        <span>{model.title}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='italic text-gray-500 dark:text-gray-400'>
                    {t('No models in this category')}
                  </p>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div className='bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:bg-slate-700 dark:text-gray-400'>
        <p>
          {t(
            'Word consumption multiplier affects how many words are counted against your subscription limit. A lower multiplier means more efficient word usage.'
          )}
        </p>
        <p className='mt-1'>
          {t(
            'Models with 0x multiplier have unlimited usage and do not count against your word limit.'
          )}
        </p>
      </div>
    </div>
  );
}
